import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { OwlMascot } from "@/components/OwlMascot";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Check, ExternalLink } from "lucide-react";

const RAZORPAY_QUARTERLY = process.env.REACT_APP_RAZORPAY_QUARTERLY_URL || "";
const RAZORPAY_YEARLY = process.env.REACT_APP_RAZORPAY_YEARLY_URL || "";

const Paywall = () => {
  const { billing, checkAuth } = useAuth();
  const [confirming, setConfirming] = useState(null);
  const navigate = useNavigate();

  const activate = async (plan) => {
    setConfirming(plan);
    try {
      await api.post("/billing/activate", { plan, payment_ref: `manual_${Date.now()}` });
      await checkAuth();
      toast.success(`${plan === "quarterly" ? "Quarterly" : "Yearly"} subscription activated!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error("Could not activate — contact support.");
    } finally {
      setConfirming(null);
    }
  };

  const goPay = (plan) => {
    const url = plan === "quarterly" ? RAZORPAY_QUARTERLY : RAZORPAY_YEARLY;
    if (!url) {
      toast("Payment link not configured yet — activating in demo mode.", { duration: 3000 });
      activate(plan);
      return;
    }
    // Open Razorpay
    const win = window.open(url, "_blank");
    if (!win) toast.error("Please allow popups to pay.");
    // Manual confirm — user comes back and clicks "I've paid"
  };

  const plans = [
    { key: "quarterly", label: "3 Months", price: 399, tag: "Most flexible", perMonth: "₹133/mo" },
    { key: "yearly", label: "1 Year", price: 1399, tag: "Best value · save 30%", perMonth: "₹117/mo" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-14">
        <div className="text-center">
          <div className="inline-block anim-float"><OwlMascot size={110} talking /></div>
          <h1 className="mt-4 font-outfit font-black text-5xl text-slate-900" data-testid="paywall-title">
            {billing?.kind === "expired" ? "Your trial has ended" : "Unlock BrimiAI Pro"}
          </h1>
          <p className="mt-3 text-slate-600 text-lg max-w-2xl mx-auto">
            Keep running experiments, generating lab reports, chatting with Brimi and collaborating live. Cancel anytime.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {plans.map((p) => (
            <div key={p.key} className={`rounded-3xl border-2 p-8 relative ${p.key === "yearly" ? "border-blue-600 bg-blue-50/40" : "border-slate-200 bg-white"}`} data-testid={`paywall-plan-${p.key}`}>
              {p.key === "yearly" && (
                <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-bold uppercase tracking-widest flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> {p.tag}
                </div>
              )}
              <div className="text-slate-500 uppercase tracking-widest text-xs font-bold">{p.label}</div>
              <div className="mt-2 font-outfit font-black text-5xl text-slate-900">₹{p.price}</div>
              <div className="text-slate-500 text-sm mt-1">{p.perMonth} · billed once</div>
              <ul className="mt-6 space-y-2 text-slate-700">
                {["All 88 experiments (P/C/B, IX–XII)", "AI tutor · Claude · GPT · Gemini", "Voice input & TTS", "Auto lab reports (PDF)", "Live collaboration rooms", "Priority Brimi tutor responses"].map((f, i) => (
                  <li key={i} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {f}</li>
                ))}
              </ul>
              <Button
                onClick={() => goPay(p.key)}
                disabled={confirming === p.key}
                className={`w-full mt-8 h-12 rounded-full text-base ${p.key === "yearly" ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"} text-white`}
                data-testid={`paywall-buy-${p.key}`}
              >
                {confirming === p.key ? "Activating..." : `Pay ₹${p.price}`}
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
              <button
                onClick={() => activate(p.key)}
                className="w-full mt-2 text-xs text-slate-500 hover:text-slate-800 font-mono"
                data-testid={`paywall-confirm-${p.key}`}
              >
                Already paid? Click to confirm.
              </button>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center text-sm text-slate-500 font-mono">
          Questions? Email{" "}
          <a className="text-blue-600 hover:underline" href="mailto:brajeshc56@gmail.com">brajeshc56@gmail.com</a>
        </div>
        <div className="mt-3 text-center">
          <Link to="/dashboard" className="text-slate-500 text-sm hover:text-slate-900">← Back to dashboard</Link>
        </div>
      </main>
    </div>
  );
};

export default Paywall;
